import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Search, ArrowRight, Star, ChefHat, Apple, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChefCard, DishCard, SectionHeader } from "@/components/Cards";
import { HomeBanners, CustomSections, Testimonials, CtaLink } from "@/components/HomeSections";
import { fetchChefs, fetchDishes, fetchCategories, resolveImg } from "@/lib/apiData";
import { useSite } from "@/context/SiteContext";

const pickFeatured = (all, ids) => (ids?.length ? all.filter((x) => ids.includes(x.id)) : all);

function Hero({ h, t }) {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const search = (e) => {
    e.preventDefault();
    navigate(`/marketplace${query ? `?q=${encodeURIComponent(query)}` : ""}`);
  };
  const title2 = t(h.hero_title_2 || "");
  const hl = t(h.hero_highlight || "").trim();
  const esc = hl.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const m = hl ? new RegExp(`(?<![\\p{L}])${esc}(?![\\p{L}])`, "iu").exec(title2) : null;
  const parts = m ? [title2.slice(0, m.index), m[0], title2.slice(m.index + m[0].length)] : null;
  return (
    <section className="relative min-h-[92vh] flex items-center grain" data-testid="home-hero">
      <div className="absolute inset-0">
        <img src={resolveImg(h.hero_image)} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#fbf9f7] via-transparent to-transparent" />
      </div>
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
          {h.hero_eyebrow && (
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#f5a623] mb-6">
              <span className="w-8 h-px bg-[#f5a623]" /> {t(h.hero_eyebrow)}
            </span>
          )}
          <h1 className="font-display text-5xl sm:text-7xl font-black tracking-tighter leading-[0.95] text-white" data-testid="hero-title">
            {t(h.hero_title)}<br />
            {parts ? <>{parts[0]}<span className="italic text-[#ff5a5a]">{parts[1]}</span>{parts[2]}</> : title2}
          </h1>
          {h.hero_tagline && <p className="mt-4 font-display italic text-2xl sm:text-3xl text-[#ff5a5a]">{t(h.hero_tagline)}</p>}
          <p className="mt-6 text-lg text-neutral-200 max-w-xl leading-relaxed">{t(h.hero_subtitle)}</p>
          <form onSubmit={search} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-xl" data-testid="hero-search-form">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t(h.search_placeholder)} data-testid="hero-search-input"
                className="h-14 pl-12 bg-white border-white text-neutral-900 rounded-none text-base focus-visible:ring-[#d00000]" />
            </div>
            <Button type="submit" data-testid="hero-search-btn" className="h-14 px-8 bg-[#d00000] hover:bg-[#a30000] text-white rounded-none text-base gap-2">
              {t(h.search_button || "Explore")} <ArrowRight className="w-4 h-4" />
            </Button>
          </form>
          <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-neutral-200">
            {(h.stats || []).map((s, i) => (
              <span key={i} className="flex items-center gap-2">{i === 0 && <Star className="w-4 h-4 fill-[#f5a623] text-[#f5a623]" />}{t(s)}</span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Promos({ promos, t }) {
  if (!promos?.length) return null;
  return (
    <section className="border-y border-neutral-200 bg-white py-4 overflow-hidden" data-testid="promo-marquee">
      <div className="marquee">
        {[0, 1].map((dup) => (
          <div className="marquee__track" key={dup} aria-hidden={dup === 1}>
            {promos.concat(promos).map((p, i) => (
              <span key={`${dup}-${i}`} className="flex items-center gap-3 whitespace-nowrap">
                <span className={`font-display text-xl font-bold ${i % 2 ? "text-[#f5a623]" : "text-[#d00000]"}`}>{t(p.title)}</span>
                <span className="text-neutral-500">{t(p.detail)}</span>
                {p.code && <span className="px-2 py-0.5 border border-neutral-300 text-xs text-neutral-900">{p.code}</span>}
                <span className="text-neutral-300">◆</span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

function Categories({ t }) {
  const { data: cats = [] } = useQuery({ queryKey: ["categories"], queryFn: fetchCategories });
  const list = cats.filter((c) => c.featured !== false);
  if (!list.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24" data-testid="home-categories">
      <SectionHeader label="Browse by origin" title="Featured cuisines"
        action={<Link to="/marketplace" className="hidden sm:flex items-center gap-2 text-sm text-neutral-500 hover:text-[#d00000] transition-colors">{t("All cuisines")} <ArrowRight className="w-4 h-4" /></Link>} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {list.map((c, i) => (
          <motion.div key={c.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.06 }}
            className={i === 0 ? "col-span-2 row-span-2" : ""}>
            <Link to={`/marketplace?cuisine=${encodeURIComponent(c.name)}`} data-testid={`cuisine-${c.id}`} className="group relative block overflow-hidden h-full min-h-[180px] border border-neutral-200">
              <img src={resolveImg(c.image)} alt={c.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-5">
                <span className="text-xs uppercase tracking-[0.2em] text-[#f5a623]">{t(c.tag)}</span>
                <h3 className={`font-display font-bold text-white ${i === 0 ? "text-3xl sm:text-4xl" : "text-xl"}`}>{t(c.name)}</h3>
                <span className="text-sm text-neutral-300">{c.count} {t("dishes")}</span>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function Ctas({ h, vis, t }) {
  if (vis.show_app_cta === false && vis.show_chef_cta === false) return null;
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid md:grid-cols-2 gap-6">
      {vis.show_app_cta !== false && (
        <div className="relative overflow-hidden border border-neutral-200 p-10 bg-white" data-testid="download-app-cta">
          <p className="text-xs uppercase tracking-[0.25em] text-[#d00000] mb-4">{t("On the go")}</p>
          <h3 className="font-display text-3xl font-bold text-neutral-900">{t(h.app_title)}</h3>
          <p className="mt-3 text-neutral-600 max-w-sm">{t(h.app_text)}</p>
          <div className="flex flex-wrap gap-3 mt-8">
            <a href={h.app_store_url || "#"} target={h.app_store_url ? "_blank" : undefined} rel="noreferrer" data-testid="app-store-btn"><Button className="bg-neutral-900 text-white hover:bg-neutral-800 rounded-none gap-2 h-12"><Apple className="w-5 h-5" /> App Store</Button></a>
            <a href={h.play_store_url || "#"} target={h.play_store_url ? "_blank" : undefined} rel="noreferrer" data-testid="play-store-btn"><Button className="bg-neutral-900 text-white hover:bg-neutral-800 rounded-none gap-2 h-12"><Play className="w-5 h-5" /> Google Play</Button></a>
          </div>
        </div>
      )}
      {vis.show_chef_cta !== false && (
        <div className="relative overflow-hidden border border-[#d00000] p-10 bg-[#d00000]" data-testid="become-chef-cta">
          <p className="text-xs uppercase tracking-[0.25em] text-white/70 mb-4">{t("Cook with us")}</p>
          <h3 className="font-display text-3xl font-bold text-white">{t(h.chef_cta_title)}</h3>
          <p className="mt-3 text-white/80 max-w-sm">{t(h.chef_cta_text)}</p>
          <CtaLink to={h.chef_cta_link} testId="become-chef-btn">
            <Button className="mt-8 bg-white text-[#d00000] hover:bg-neutral-100 rounded-none gap-2 h-12"><ChefHat className="w-5 h-5" /> {t(h.chef_cta_button)}</Button>
          </CtaLink>
        </div>
      )}
    </section>
  );
}

export default function Home() {
  const { config, t } = useSite();
  const { data: chefs = [] } = useQuery({ queryKey: ["chefs"], queryFn: () => fetchChefs() });
  const { data: dishes = [] } = useQuery({ queryKey: ["dishes"], queryFn: () => fetchDishes() });
  if (!config) return <div className="min-h-[92vh] bg-neutral-900" data-testid="home-page" />;
  const h = config.homepage;
  const vis = config.visibility || {};
  const featuredChefs = pickFeatured(chefs, h.featured_chef_ids);
  const featuredDishes = pickFeatured(dishes, h.featured_dish_ids).slice(0, 8);

  return (
    <div data-testid="home-page">
      <Hero h={h} t={t} />
      {vis.show_promos !== false && <Promos promos={h.promos} t={t} />}
      <HomeBanners items={config.banners?.items} t={t} />
      {vis.show_categories !== false && <Categories t={t} />}

      {vis.show_featured_chefs !== false && featuredChefs.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12" data-testid="home-featured-chefs">
          <SectionHeader label="The talent" title="Featured chefs & restaurants"
            action={<Link to="/chefs" className="hidden sm:flex items-center gap-2 text-sm text-neutral-500 hover:text-[#d00000] transition-colors">{t("See all")} <ArrowRight className="w-4 h-4" /></Link>} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredChefs.map((c, i) => <ChefCard key={c.id} chef={c} index={i} />)}
          </div>
        </section>
      )}

      {vis.show_featured_dishes !== false && featuredDishes.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24" data-testid="home-featured-dishes">
          <SectionHeader label="Straight from the pot" title="Dishes people are loving" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredDishes.map((d, i) => <DishCard key={d.id} dish={d} index={i} />)}
          </div>
        </section>
      )}

      {vis.show_custom_sections !== false && <CustomSections sections={h.sections} t={t} />}

      {vis.show_spotlight !== false && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16" data-testid="home-spotlight">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7 relative overflow-hidden aspect-[16/10] group">
              <img src={resolveImg(h.spotlight_image)} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent" />
            </div>
            <div className="md:col-span-5">
              <p className="text-xs uppercase tracking-[0.25em] text-[#d00000] mb-4">{t(h.spotlight_label)}</p>
              <h2 className="font-display text-4xl sm:text-5xl font-black text-neutral-900 leading-none">{t(h.spotlight_title)}</h2>
              <p className="mt-5 text-neutral-600 leading-relaxed">{t(h.spotlight_text)}</p>
              <CtaLink to={h.spotlight_link} testId="jollof-spotlight-btn">
                <Button className="mt-8 bg-transparent border border-neutral-300 hover:bg-neutral-100 text-neutral-900 rounded-none gap-2">{t(h.spotlight_button)} <ArrowRight className="w-4 h-4" /></Button>
              </CtaLink>
            </div>
          </div>
        </section>
      )}

      {vis.show_testimonials !== false && h.testimonials?.length > 0 && (
        <section className="bg-[#f4efec] border-y border-neutral-200 py-24" data-testid="home-testimonials">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <SectionHeader label="From the table" title="What eaters are saying" />
            <Testimonials items={h.testimonials} t={t} />
          </div>
        </section>
      )}

      <Ctas h={h} vis={vis} t={t} />
    </div>
  );
}
